// The arrival half of an attributed notification link.
//
// Every notification CTA carries `?n=<runId>&r=<deliveryId>`, stamped per
// recipient by the server (`attributeCtaUrl` in `notification-links.js`). This
// reads that pair off the landing page, reports it once, and removes it from
// the address bar so the parameters are never bookmarked, shared or re-sent.
//
// Without this half a click is indistinguishable from any other navigation, and
// every outcome number about notifications is an intent-to-treat estimate with
// no mechanism behind it.

import instance from '@/api/instance.js';

/** The parameter names the server stamps. Kept in sync with
 *  `ATTRIBUTION_PARAMS` in `strum-server/services/notification-links.js`. */
const RUN_PARAM = 'n';
const DELIVERY_PARAM = 'r';

/**
 * Deliveries already reported in this page session.
 *
 * The guard is per-tab and in-memory on purpose: a duplicate would be collapsed
 * server-side anyway (`clickedAt` is written with `$min`), so this exists to
 * avoid a pointless request on a back-navigation, not to enforce correctness.
 */
const reported = new Set<string>();

/**
 * Records the click, then strips the parameters from the URL.
 *
 * The strip happens whether or not the report succeeds. A stale or unreportable
 * link should still not leave tracking parameters sitting in the address bar,
 * and the alternative — retrying on the next navigation — would report a click
 * against a page the person reached some other way.
 */
export function recordNotificationArrival(isAuthorized: boolean): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const runId = params.get(RUN_PARAM);
  const deliveryId = params.get(DELIVERY_PARAM);

  if (!deliveryId) return;

  // Strip first. An unauthenticated arrival — a link opened in a browser that
  // is not signed in — cannot be attributed to anyone, but the parameters
  // should still not survive into the URL the person then signs in from.
  stripParams(params);

  if (!isAuthorized || reported.has(deliveryId)) return;
  reported.add(deliveryId);

  // Not awaited and never surfaced: this is telemetry riding alongside a
  // navigation the person actually cares about.
  instance
    .post('/notifications/click', {
      deliveryId,
      ...(runId && { runId })
    })
    .catch(() => {});
}

/** Rewrites the current URL without the attribution parameters, preserving
 *  everything else and leaving no extra history entry to go "back" through. */
function stripParams(params: URLSearchParams): void {
  params.delete(RUN_PARAM);
  params.delete(DELIVERY_PARAM);

  const query = params.toString();
  const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;

  try {
    window.history.replaceState(window.history.state, '', next);
  } catch {
    // A replaceState can throw on an exotic origin. The parameters are inert
    // once reported, so leaving them in the bar is not worth failing over.
  }
}
